import { useState } from 'react';
import qs from 'qs';
import { valorantRanks } from '../../constants/rank-and-regions';
import { omit } from 'lodash';

export function useSynchronizedQueryStringForSearchForPackages({
  automaticallyManagedRefinementLists,
  automaticallyManagedRanges,
}) {
  const [searchState, setSearchState] = useState(() =>
    urlToSearchState(window.location, {
      automaticallyManagedRefinementLists,
      automaticallyManagedRanges,
    })
  );
  const [debouncedSetState, setDebouncedSetState] = useState(null);

  function onSearchStateChange(newSearchState) {
    clearTimeout(debouncedSetState);

    setDebouncedSetState(
      setTimeout(() => {
        const url = new URL(window.location.href);
        const urlParts = url.href.split('/');

        history.replaceState(
          null,
          null,
          window.location.origin +
            '/' +
            urlParts[3] +
            '/' +
            urlParts[4].split('?')[0] +
            searchStateToUrl(newSearchState)
        );
      }, 400)
    );

    setSearchState(newSearchState);
  }

  return {
    searchState,
    onSearchStateChange,
  };
}

/**
 * Extracts an initial search state from the URL
 * @param {URL} url
 * @param {string[]} automaticallyManagedRefinementLists List of all the keys that represent a refinement list, and that
 *  can automatically be extracted from the QS
 * @param {string[]} automaticallyManagedRanges List of all the keys that represent a range, and that can automatically
 *  be extracted from the QS
 * @returns a searchState that can be fed to InstantSearch
 */
export function urlToSearchState(
  url,
  { automaticallyManagedRefinementLists, automaticallyManagedRanges }
) {
  const parsedQs = qs.parse(url.search.slice(1));

  const searchState = {
    refinementList: {},
    range: {},
  };

  // Extract the textual query
  if (parsedQs.query) {
    searchState.query = parsedQs.query;
  }

  // Extract the page
  if (parsedQs.page) {
    searchState.page = parsedQs.page;
  }

  // Extract all possible refinement lists from the URL
  automaticallyManagedRefinementLists.forEach((key) => {
    const valuesString = parsedQs[key];

    if (!valuesString) {
      return;
    }

    searchState.refinementList[key] = valuesString.split(',');
  });

  // Extract all possible ranges from the URL
  automaticallyManagedRanges.forEach((key) => {
    const rangeString = parsedQs[key];

    if (!rangeString) {
      return;
    }

    const [min, max] = rangeString.split(',');

    searchState.range[key] = {
      min: Number(min) || undefined,
      max: Number(max) || undefined,
    };
  });

  // Additional custom logic that handles parsing the region/tier
  parseRegionOrTierFromPath(url, searchState);

  return searchState;
}

function parseRegionOrTierFromPath(url, searchStateToMutate) {
  const matches =
    /\/valorant\/valorant-account\/category\/(unranked|ranked)(?:\/([^\/]+))?(?:\/([^\/]+))?$/.exec(
      url.pathname
    );

  if (!matches) {
    return;
  }

  const ranked = matches[1] === 'ranked';
  const params = matches.slice(2).filter((item) => item !== undefined);

  let regions = null;
  let tiers = null;
  let regionsOrTiers = null;

  // URL is of the `.../unranked/...` form
  if (!ranked) {
    tiers = ['Unranked'];
  }

  // URL is of the `.../unranked/[regions]` form
  if (!ranked && params.length === 1) {
    regions = params[0].split('+');
  }

  // URL is of the `.../ranked/[regions]/[ranks]` form
  if (ranked && params.length === 2) {
    regions = params[0].split('+');
    tiers = params[1].split('+');
  }

  // URL is of the `.../ranked/[regions or ranks]` form
  if (ranked && params.length === 1) {
    regionsOrTiers = params[0].split('+');
    if (valorantRanks.includes(regionsOrTiers[0])) {
      tiers = regionsOrTiers;
    } else {
      regions = regionsOrTiers;
    }
  }

  if (regions) {
    searchStateToMutate.refinementList['region_name'] = regions;
  }

  if (tiers) {
    // Depending on which page we're on, we don't filter on ranks the same way.
    searchStateToMutate.refinementList['tier'] = tiers;
  }
}

/**
 * Transform a search state to a query string
 * @param searchState
 * @returns a query string
 */
export function searchStateToUrl(searchState) {
  const qsObject = {};

  // Serialize the textual query
  if (searchState.query) {
    qsObject.query = searchState.query;
  }

  // Serialize the page
  if (searchState.page !== 1) {
    qsObject.page = searchState.page;
  }

  // Serialize all refinement lists
  for (const key in omit(searchState.refinementList, [
    'region_name',
    'tier',
    'rank.tier',
  ])) {
    const value = searchState.refinementList[key];

    if (value.length === 0) {
      continue;
    }

    qsObject[key] = value.join(',');
  }

  // Serialize all ranges
  for (const key in searchState.range) {
    const { min, max } = searchState.range[key];

    if (min === undefined && max === undefined) {
      continue;
    }

    qsObject[key] = `${min ?? ''},${max ?? ''}`;
  }

  const path = serializeRegionOrTierToPathname(searchState);

  let result = path;
  const queryString = qs.stringify(qsObject);
  if (queryString) {
    result += '?';
    result += queryString;
  }

  return result;
}

function serializeRegionOrTierToPathname(searchState) {
  const regionName = searchState.refinementList?.region_name;
  const serializedRegionName = regionName ? regionName.join('+') : '';

  const tier =
    searchState.refinementList?.tier ||
    searchState.refinementList?.['rank.tier'];
  const serializedTier = tier ? tier.join('+') : '';

  const isUnranked = new RegExp('unranked').test(window.location.href);

  let path = '';
  path += isUnranked ? '/category/unranked' : '/category/ranked';

  if (serializedRegionName) {
    path += '/';
    path += serializedRegionName;
  }

  if (!isUnranked && serializedTier) {
    path += '/';
    path += serializedTier;
  }

  return path;
}

export function createURL(searchState) {
  return '/valorant' + searchStateToUrl(searchState);
}
